import type { ReactNode } from 'react';
import { Toast } from '@base-ui/react/toast';
import { Icon } from '@/ui/kit/Icon';
import styles from './primitives.module.css';

import { useT } from '@/i18n/languageStore';

export interface ToastOptions {
  title: string;
  description?: string;
  /** Milliseconds; default 5000. */
  timeout?: number;
  /** `high` interrupts screen readers (errors); default polite. */
  priority?: 'low' | 'high';
}

function Toasts() {
  const t = useT();
  const { toasts } = Toast.useToastManager();
  return (
    <Toast.Portal>
      <Toast.Viewport className={styles.toastViewport} aria-label={t('Notifications')}>
        {toasts.map((toast) => (
          <Toast.Root key={toast.id} toast={toast} className={styles.toast}>
            <div className={styles.toastText}>
              <Toast.Title className={styles.toastTitle} />
              <Toast.Description className={styles.toastDescription} />
            </div>
            <Toast.Close className={styles.toastClose} aria-label={t('Dismiss')}>
              <Icon name="close" size={18} />
            </Toast.Close>
          </Toast.Root>
        ))}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

/** Brief confirmations ("Report sent") announced in a live region. Mounted once at the app root. */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider timeout={5000}>
      {children}
      <Toasts />
    </Toast.Provider>
  );
}

export function useToast() {
  const manager = Toast.useToastManager();
  return {
    show: ({ title, description, timeout, priority }: ToastOptions) =>
      manager.add({ title, description, timeout, priority }),
  };
}
